import { jwtDecode, JwtPayload } from 'jwt-decode';

/**
 * Checks if the token is still valid by checking its expiration time
 *
 * @param accessToken the token to check
 * @return true if valid
 */
export const isTokenValid = (accessToken?: string | undefined): boolean => {
    if (!accessToken) {
        return false;
    }

    try {
        const jwtPayload = jwtDecode<JwtPayload>(accessToken);
        return !!jwtPayload?.exp && jwtPayload.exp * 1000 > Date.now();
    } catch (error) {
        // Just return false if the JWT token has the wrong format
        return false;
    }
};

/**
 * Attempts to extract a claim parameter from the token.
 *
 * @param accessToken the token to extract information from
 * @param claim the name of the claim to extract
 * @returns the value if found or undefined
 */
export const extractTokenClaim = (accessToken: string, claim: string): string | undefined => {
    try {
        const jwtPayload = jwtDecode<JwtPayload>(accessToken);
        return (jwtPayload as any)?.[claim];
    } catch (error) {
        return;
    }
};

/**
 * Extracts a government id from the token's `sub` claim, which oba-auth-service formats as
 * `<countryCode><governmentId>` (e.g. "DK1234567890") -- the same format
 * consumerloan-application-service's UserJwtExtractor.getGovernmentId parses server-side.
 *
 * @param accessToken the token to extract the government id from
 * @param countryCode the 2-letter ISO country code prefix to strip (e.g. CountryCode.DK)
 * @returns the government id if the subject is present, prefixed with countryCode, and non-empty; otherwise undefined
 */
export const extractGovernmentIdFromSubClaim = (accessToken: string, countryCode: string): string | undefined => {
    const subject = extractTokenClaim(accessToken, 'sub');
    if (!subject?.startsWith(countryCode)) {
        return undefined;
    }
    return subject.slice(countryCode.length) || undefined;
};
